import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from 'appconfig';
import {
  getCurrentMACContactEmail,
  selectGenericPropertyByFullKey,
  selectGetCustomerByEmail,
  selectHasSourceDownloads,
  selectIsArchivedAccount,
  selectIsMACSiteOnConcession,
  selectIsRenewableAccount,
  selectIsUpgradableAccount,
} from 'modules/mac/duck/selectors';
import { State } from 'model/State';
import {
  fetchIsSiteOnConcession,
  RESET_ARCHIVE_UNARCHIVE_ACCOUNT,
} from 'modules/mac/duck/actions/accountId';
import { RESET_ARCHIVE_UNARCHIVE_LICENSE } from 'modules/mac/duck/actions/license';
import { selectIsArchivedLicense } from 'modules/mac/duck/selectors/license';
import { fetchHasSourceDownloads } from 'modules/mac/duck/actions/downloads';
import { isAtlassianOrganisation, isFutureEndDate } from 'util/mac/helpers';
import { HostedNonAtlassianOrganizationAccount } from './ActionsSectionElements/HostedNonAtlassianOrganizationAccount';
import { NotHostedAccount } from './ActionsSectionElements/NotHostedAccount';
import { HostedAtlassianOrganizationAccount } from './ActionsSectionElements/HostedAtlassianOrganizationAccount';
import { EndUserAccount } from './ActionsSectionElements/EndUserAccount';

type PropTypes = {
  isAccount: boolean;
  accountDetails: any;
  isEndUserLicenses?: boolean;
  isArchived: boolean;
  isActivePartner?: boolean;
};

const ActionsSection: React.FC<PropTypes> = ({
  isEndUserLicenses,
  isAccount,
  accountDetails,
  isArchived,
  isActivePartner,
}) => {
  const productPackage = accountDetails.merchandise.productPackage || accountDetails.productPackage;
  const {
    isUnclaimedCloudSite,
    id: accountId,
    periods,
    contactRelationships,
    renewalAction,
    latestPeriod: { endDate },
  } = accountDetails;
  const { key, hostingType, downloadProduct } = productPackage;

  const genericPropertyFullKey = `related.source.product.${downloadProduct?.key}`;

  const dispatch = useDispatch();
  const isSiteOnConcession = useSelector((state) =>
    selectIsMACSiteOnConcession(state as State, accountId),
  );
  const currentContactEmail = useSelector(getCurrentMACContactEmail);
  const customerByEmail = useSelector(selectGetCustomerByEmail);
  const isAccountSuccessfullyArchived = useSelector(selectIsArchivedAccount);
  const isLicenseSuccessfullyArchived = useSelector(selectIsArchivedLicense);
  const isRenewableAccount = useSelector((state: State) =>
    selectIsRenewableAccount(state, accountId),
  );
  const isUpgradableAccount = useSelector((state: State) =>
    selectIsUpgradableAccount(state, accountId),
  );
  const genericPropertyTextValue = useSelector((state: State) =>
    selectGenericPropertyByFullKey(state, genericPropertyFullKey),
  );
  const hasSourceDownloads = useSelector(selectHasSourceDownloads);

  const [isArchiveButtonVisible, setIsArchiveButtonVisible] = useState(false);

  const [keysList, setKeysList] = useState<string[]>([]);
  const [showSourceDownloadBtn, setShowSourceDownloadBtn] = useState(false);

  const isRenewalActionNone = renewalAction === 'NONE';

  const handleArchiveUnarchiveRedirect = () => {
    if (isArchived) {
      window.location.href = `${config.root}/my/licenses/${accountId}`;
    } else {
      window.location.href = `${config.root}/my`;
    }
  };

  useEffect(() => {
    if (isUnclaimedCloudSite && isFutureEndDate(endDate)) {
      dispatch(fetchIsSiteOnConcession(accountId));
    }
  }, []);

  useEffect(() => {
    if (isAccountSuccessfullyArchived === '') {
      handleArchiveUnarchiveRedirect();
      dispatch({
        type: RESET_ARCHIVE_UNARCHIVE_ACCOUNT,
      });
    }
  }, [isAccountSuccessfullyArchived]);

  useEffect(() => {
    if (isLicenseSuccessfullyArchived === '') {
      handleArchiveUnarchiveRedirect();
      dispatch({
        type: RESET_ARCHIVE_UNARCHIVE_LICENSE,
      });
    }
  }, [isLicenseSuccessfullyArchived]);

  useEffect(() => {
    if (currentContactEmail) {
      if (
        periods?.some(
          ({ partnerId }) =>
            partnerId ===
            (customerByEmail[currentContactEmail] as any)?._result?.contactDetails?.id,
        ) ||
        contactRelationships.some(
          ({ contact }) => contact.contactDetails.email === currentContactEmail,
        )
      ) {
        setIsArchiveButtonVisible(true);
      }
    }
  }, [currentContactEmail, customerByEmail]);

  useEffect(() => {
    if (genericPropertyTextValue?.length) {
      const keys: string[] = [...genericPropertyTextValue.map(({ textValue }) => textValue)];
      setKeysList(keys);

      genericPropertyTextValue?.forEach((genericPropertyItem) => {
        dispatch(fetchHasSourceDownloads(genericPropertyItem.textValue));
      });
    } else if (downloadProduct?.key && !hasSourceDownloads?.[downloadProduct.key]?.inProgress) {
      setKeysList([downloadProduct.key]);
      dispatch(fetchHasSourceDownloads(downloadProduct.key));
    }
  }, [genericPropertyTextValue, downloadProduct?.key]);

  useEffect(() => {
    if (hasSourceDownloads && keysList.length) {
      keysList.forEach((productKey) => {
        if (hasSourceDownloads[productKey] && hasSourceDownloads[productKey]._result === true) {
          setShowSourceDownloadBtn(true);
        }
      });
    }
  }, [hasSourceDownloads, keysList.length]);

  if (isEndUserLicenses) {
    return (
      <EndUserAccount
        isRenewableAccount={isRenewableAccount}
        isUpgradableAccount={isUpgradableAccount}
        isArchived={isArchived}
        isAccount={isAccount}
        accountDetails={accountDetails}
        productPackage={productPackage}
        isActivePartner={isActivePartner}
      />
    );
  }

  if (hostingType === 'HOSTED') {
    if (isAtlassianOrganisation(key)) {
      return <HostedAtlassianOrganizationAccount accountDetails={accountDetails} />;
    }
    return (
      <HostedNonAtlassianOrganizationAccount
        isSiteOnConcession={isSiteOnConcession}
        isArchiveButtonVisible={isArchiveButtonVisible}
        isArchived={isArchived}
        isAccount={isAccount}
        productKey={key}
        isRenewalActionNone={isRenewalActionNone}
        isRenewableAccount={isRenewableAccount}
        accountDetails={accountDetails}
      />
    );
  }

  return (
    <NotHostedAccount
      isSiteOnConcession={isSiteOnConcession}
      isRenewableAccount={isRenewableAccount}
      isUpgradableAccount={isUpgradableAccount}
      showSourceDownloadBtn={showSourceDownloadBtn}
      isArchiveButtonVisible={isArchiveButtonVisible}
      isArchived={isArchived}
      isAccount={isAccount}
      accountDetails={accountDetails}
      productPackage={productPackage}
    />
  );
};

export default ActionsSection;
